import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Columns, Config, DefaultConfig } from 'ngx-easy-table';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { StatisticsService } from '../../api/backend/services/statistics/statistics.service';
import { UtilsService } from '../../lib/utils.service';
import {
  ExportStatistics,
  OngoingStatisticExportFile,
  OngoingStatisticExportFiles,
} from '../../models/backend/statistics/export-statistics';
import { ThingType } from '../../models/thingtype';
import { NotificationService } from '../../shared/notification.service';

@Component({
  selector: 'app-list-exports',
  templateUrl: './list-exports.component.html',
  styleUrl: './list-exports.component.scss',
})
export class ListExportsComponent implements OnInit, OnChanges {
  statisticsService: StatisticsService = inject(StatisticsService);
  notificationService: NotificationService = inject(NotificationService);
  utilsService: UtilsService = inject(UtilsService);

  @Input({ required: true }) thingType?: ThingType;

  exports: OngoingStatisticExportFiles = [];

  loading = false;

  configuration: Config = {
    ...DefaultConfig,
    searchEnabled: true,
    paginationEnabled: true,
    orderEnabled: true,
    rows: 50,
  };

  columns: Columns[] = [
    { key: 'date', title: 'Date', width: ' ' },
    { key: 'filename', title: 'Name', width: '70%' },
    { key: 'size', title: 'Size', width: ' ' },
    { key: 'actions', title: '', width: ' ' },
  ];

  formGroupLocalFilters = new FormGroup({
    date: new FormControl(),
    filename: new FormControl(),
  });

  ngOnInit(): void {
    this.loadExports();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['thingType'] && changes['thingType'].currentValue !== changes['thingType'].previousValue) {
      this.loadExports();
    }
  }

  loadExports(): void {
    if (!this.thingType) {
      this.exports = [];
      return;
    }

    this.setLoading(true);
    this.statisticsService
      .listExports(this.thingType)
      .pipe(
        catchError((err) => {
          this.notificationService.showError(err.message, err);
          return of([]);
        }),
        map((exports) =>
          exports.map((item) => ({
            ...item,
            date: this.utilsService.formatDate(item.date),
          })),
        ),
      )
      .subscribe({
        next: (exports) => {
          this.exports = exports;
        },
        complete: () => {
          this.setLoading(false);
        },
      });
  }

  setLoading(isLoading: boolean): void {
    this.loading = isLoading;
    this.configuration.isLoading = isLoading;
  }

  downloadExportFile(key: string): void {
    if (!this.thingType) return;

    this.statisticsService
      .getExportFileUrl(this.thingType, key)
      .pipe(
        catchError((err) => {
          console.error(err.message, err);
          this.notificationService.showError(err.message ?? 'File does not exist', err);
          return of(null);
        }),
      )
      .subscribe((value: ExportStatistics | null) => {
        if (value) {
          window.open(value.presignedUrl, '_blank');
        }
      });
  }

  typedOngoingStatisticExportFile(item: unknown): OngoingStatisticExportFile {
    return item as OngoingStatisticExportFile;
  }
}
